import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageCheck, AlertTriangle, FileSignature, ReceiptText, Sparkles, Check, Gift, Wrench, Lightbulb } from "lucide-react";
import api from "@/lib/api";

const BASE = { lead: "leads", task: "tasks", support: "support" };
const TONE = {
  offen_pruefen: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  im_paket: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  kulanz: "border-sky-400/40 bg-sky-400/10 text-sky-300",
  zusatzarbeit: "border-orange-400/40 bg-orange-400/10 text-orange-300",
  angebot_noetig: "border-violet-400/40 bg-violet-400/10 text-violet-300",
  zusatzmodul_empfohlen: "border-[#A3E635]/40 bg-[#A3E635]/10 text-[#A3E635]",
  nicht_verrechenbar: "border-white/15 bg-white/5 text-gray-400",
};
const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors";
const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH")}`;

export default function PaketPruefung({ entityType, entity, onChanged }) {
  const base = BASE[entityType];
  const cid = entity.client_id;
  const [cfg, setCfg] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [sug, setSug] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    billing_status: entity.billing_status || "offen_pruefen",
    estimated_value: entity.estimated_value ?? "",
    customer_benefit: entity.customer_benefit || "",
    recommended_module_id: entity.recommended_module_id || "",
    billing_note: entity.billing_note || "",
  });

  useEffect(() => {
    api.get("/billing/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get(`/billing/suggest`, { params: { entity_type: entityType, eid: entity.id } }).then((r) => setSug(r.data)).catch(() => {});
    if (cid) api.get(`/billing/context/${cid}`).then((r) => setCtx(r.data)).catch(() => {});
  }, [entity.id, cid, entityType]);

  useEffect(() => {
    setForm({
      billing_status: entity.billing_status || "offen_pruefen",
      estimated_value: entity.estimated_value ?? "",
      customer_benefit: entity.customer_benefit || "",
      recommended_module_id: entity.recommended_module_id || "",
      billing_note: entity.billing_note || "",
    });
  }, [entity]);

  const save = async (override = {}) => {
    setBusy(true);
    try {
      const payload = { ...form, ...override };
      payload.estimated_value = payload.estimated_value === "" ? null : Number(payload.estimated_value);
      await api.put(`/${base}/${entity.id}`, payload);
      toast.success("Paketprüfung gespeichert");
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Konnte nicht speichern."); }
    finally { setBusy(false); }
  };
  const quick = (billing_status) => { setForm((f) => ({ ...f, billing_status })); save({ billing_status }); };

  const prepareOffer = async () => {
    setBusy(true);
    try {
      await api.post(`/${base}/${entity.id}/prepare-offer`, {
        recommended_module_id: form.recommended_module_id || null,
        estimated_value: form.estimated_value === "" ? null : Number(form.estimated_value),
        benefit: form.customer_benefit || null,
      });
      toast.success("Angebot vorbereitet — in 'Nächste Schritte' sichtbar.");
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Konnte Angebot nicht vorbereiten."); }
    finally { setBusy(false); }
  };
  const prepareInvoice = async () => {
    setBusy(true);
    try {
      await api.post(`/${base}/${entity.id}/prepare-invoice`, {
        amount_chf: form.estimated_value === "" ? null : Number(form.estimated_value),
      });
      toast.success("Rechnungs-Entwurf erstellt (nicht gesendet).");
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Konnte Rechnung nicht vorbereiten."); }
    finally { setBusy(false); }
  };

  const pickModule = (mid) => {
    const m = (ctx?.recommendable_modules || []).find((x) => x.id === mid);
    setForm((f) => ({
      ...f, recommended_module_id: mid,
      customer_benefit: f.customer_benefit || (m?.benefit || ""),
      estimated_value: f.estimated_value || (m?.price_chf ?? ""),
    }));
  };

  if (!cfg) return null;
  const bs = form.billing_status;

  return (
    <div className="bg-[#0E0E12] border border-white/10 rounded-2xl p-5" data-testid="paket-pruefung">
      <div className="flex items-center gap-2 mb-1">
        <PackageCheck className="w-4 h-4 text-[#A3E635]" />
        <h3 className="font-bold tracking-tight">Paket & Leistung prüfen</h3>
      </div>
      <p className="text-[11px] text-gray-500 mb-4">Keine Arbeit ohne Paketprüfung — gehört das zum Paket oder ist es Zusatzumsatz?</p>

      {/* Kontext */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white/[0.03] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Aktuelles Paket</div>
          <div className="font-bold mt-0.5" data-testid="pp-package">{ctx?.package?.name || "—"}</div>
        </div>
        <div className="bg-white/[0.03] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Aktive Module</div>
          <div className="text-sm mt-0.5 text-gray-300">{(ctx?.active_modules || []).length === 0 ? "keine" : (ctx.active_modules.map((m) => m.name).join(", "))}</div>
        </div>
      </div>

      {bs === "offen_pruefen" && (
        <div className="flex items-start gap-2 text-sm text-amber-300 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 mb-4" data-testid="pp-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          Paketprüfung offen — bitte vor Umsetzung klären.
        </div>
      )}
      {sug && sug.status !== bs && (
        <button onClick={() => quick(sug.status)} data-testid="pp-suggestion"
          className="w-full text-left flex items-start gap-2 text-xs text-gray-300 bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2 mb-4 transition-colors">
          <Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-[#A3E635]" />
          <span><b>Vorschlag:</b> {cfg.statuses[sug.status]} — {sug.reason} <span className="text-[#A3E635] font-bold">Übernehmen</span></span>
        </button>
      )}

      {/* Verrechnungsstatus */}
      <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Verrechnungsstatus</label>
      <select value={bs} onChange={(e) => setForm({ ...form, billing_status: e.target.value })} data-testid="pp-status" className={`${inp} mt-1 ${TONE[bs] || ""}`}>
        {cfg.order.map((s) => <option key={s} value={s} className="bg-black text-white">{cfg.statuses[s]}</option>)}
      </select>

      <div className="flex gap-2 mt-2 flex-wrap">
        <QuickBtn icon={Check} testid="pp-quick-paket" onClick={() => quick("im_paket")}>Im Paket</QuickBtn>
        <QuickBtn icon={Gift} testid="pp-quick-kulanz" onClick={() => quick("kulanz")}>Kulanz</QuickBtn>
        <QuickBtn icon={Wrench} testid="pp-quick-zusatz" onClick={() => quick("zusatzarbeit")}>Zusatzarbeit</QuickBtn>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Geschätzter Wert (CHF)</label>
          <input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} data-testid="pp-value" className={`${inp} mt-1`} placeholder="0" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Zusatzmodul empfehlen</label>
          <select value={form.recommended_module_id} onChange={(e) => pickModule(e.target.value)} data-testid="pp-module" className={`${inp} mt-1`}>
            <option value="">— kein Modul —</option>
            {(ctx?.recommendable_modules || []).map((m) => <option key={m.id} value={m.id}>{m.name}{m.on_request ? " (auf Anfrage)" : ` · ${chf(m.price_chf)}`}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Kunden-Nutzen</label>
        <input value={form.customer_benefit} onChange={(e) => setForm({ ...form, customer_benefit: e.target.value })} data-testid="pp-benefit" className={`${inp} mt-1`} placeholder="Was bringt es dem Kunden?" />
      </div>
      <div className="mt-3">
        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Interne Notiz (nur KEWAG)</label>
        <textarea value={form.billing_note} onChange={(e) => setForm({ ...form, billing_note: e.target.value })} data-testid="pp-note" rows={2} className={`${inp} mt-1`} placeholder="Interne Kalkulation / Hinweis..." />
      </div>

      {Number(form.estimated_value) > 0 && (
        <div className="mt-3 text-sm text-gray-300">Möglicher Umsatz: <span className="font-bold text-[#A3E635]" data-testid="pp-revenue">{chf(form.estimated_value)}</span> <span className="text-gray-500">(inkl. 8.1% MWST)</span></div>
      )}

      <button onClick={() => save()} disabled={busy} data-testid="pp-save"
        className="mt-4 w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 rounded-lg disabled:opacity-50 transition-colors">Paketprüfung speichern</button>

      <div className="grid grid-cols-2 gap-2 mt-2">
        <button onClick={prepareOffer} disabled={busy} data-testid="pp-prepare-offer"
          className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold py-2.5 rounded-lg disabled:opacity-50">
          <FileSignature className="w-4 h-4" /> Angebot vorbereiten
        </button>
        <button onClick={prepareInvoice} disabled={busy} data-testid="pp-prepare-invoice"
          className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold py-2.5 rounded-lg disabled:opacity-50">
          <ReceiptText className="w-4 h-4" /> Rechnung / bexio
        </button>
      </div>
      {entity.offer_id && <div className="text-[11px] text-[#A3E635] mt-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Angebot vorbereitet (in "Nächste Schritte").</div>}
      {entity.invoice_id && <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-1"><ReceiptText className="w-3 h-3" /> Rechnungs-Entwurf erstellt.</div>}
    </div>
  );
}

const QuickBtn = ({ icon: Icon, children, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 hover:bg-white/10 text-gray-200 transition-colors">
    <Icon className="w-3.5 h-3.5" /> {children}
  </button>
);
